import {
  collection,
  doc,
  documentId,
  getCountFromServer,
  getDocFromServer,
  getDocs,
  getDocsFromServer,
  limit,
  orderBy,
  query,
  startAfter,
  where,
  type DocumentData,
  type QueryConstraint,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, firestore, functions } from '../firebase';
import {
  type ProductFeedbackAttachmentMetadata,
  type ProductFeedbackCreateInput,
  type ProductFeedbackDiagnosticsPayload,
  type ProductFeedbackReport,
  type ProductFeedbackStatus,
  type ProductFeedbackStatusUpdateInput,
} from '../types/productFeedback';
import { normalizeProductFeedbackTimestamp } from '../utils/productFeedbackTimestamp';

export interface ProductFeedbackCreateResult {
  reportId: string;
  status: ProductFeedbackStatus;
  statusVersion: number;
}

export interface ProductFeedbackCanonicalReadback extends ProductFeedbackCreateResult {
  createdBy: string;
}

export type ProductFeedbackListFilter = ProductFeedbackStatus | 'all';
export type ProductFeedbackAccessScope = 'reporter' | 'admin';
export type ProductFeedbackPageCursor = QueryDocumentSnapshot<DocumentData>;

export interface ProductFeedbackPage {
  reports: ProductFeedbackReport[];
  nextCursor: ProductFeedbackPageCursor | null;
}

export type ProductFeedbackStatusCounts = Record<ProductFeedbackStatus, number>;

export const PRODUCT_FEEDBACK_PAGE_SIZE = 40;
export const REPORTER_PRODUCT_FEEDBACK_PAGE_SIZE = 20;

export const isValidProductFeedbackReportId = (value: string): boolean =>
  /^[A-Za-z0-9_-]{8,128}$/.test(value);

const PRODUCT_FEEDBACK_STATUSES = new Set<ProductFeedbackStatus>([
  'open',
  'investigating',
  'resolved',
]);

const isCreateResult = (value: unknown): value is ProductFeedbackCreateResult => {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.reportId === 'string' &&
    candidate.reportId.length > 0 &&
    PRODUCT_FEEDBACK_STATUSES.has(candidate.status as ProductFeedbackStatus) &&
    Number.isInteger(candidate.statusVersion) &&
    Number(candidate.statusVersion) >= 1
  );
};

const timestampToIso = (value: unknown): string => {
  const normalized = normalizeProductFeedbackTimestamp(value);
  if (normalized) return normalized;
  throw new Error('Canonical Product Feedback timestamp is invalid.');
};

const nullableTimestampToIso = (value: unknown): string | null =>
  normalizeProductFeedbackTimestamp(value);

const normalizeAttachment = (value: unknown): ProductFeedbackAttachmentMetadata | null => {
  if (!value) return null;
  if (typeof value !== 'object') return value as ProductFeedbackAttachmentMetadata;
  const attachment = value as Record<string, unknown>;
  return {
    ...attachment,
    attachedAt: nullableTimestampToIso(attachment.attachedAt),
  } as ProductFeedbackAttachmentMetadata;
};

const parseReport = (id: string, input: unknown): ProductFeedbackReport => {
  if (!input || typeof input !== 'object') {
    throw new Error('Canonical Product Feedback report is invalid.');
  }
  const data = input as Record<string, any>;
  if (
    data.schemaVersion !== 1 ||
    !PRODUCT_FEEDBACK_STATUSES.has(data.status) ||
    !Number.isInteger(data.statusVersion) ||
    typeof data.category !== 'string' ||
    typeof data.description !== 'string' ||
    typeof data.createdBy !== 'string' ||
    !data.runtime ||
    !data.reporterContext
  ) {
    throw new Error('Canonical Product Feedback report is inconsistent.');
  }

  return {
    id,
    schemaVersion: 1,
    category: data.category,
    status: data.status,
    statusVersion: data.statusVersion,
    description: data.description,
    expectedBehavior: typeof data.expectedBehavior === 'string' ? data.expectedBehavior : null,
    actualBehavior: typeof data.actualBehavior === 'string' ? data.actualBehavior : null,
    reproductionNotes: typeof data.reproductionNotes === 'string' ? data.reproductionNotes : null,
    createdBy: data.createdBy,
    reporterContext: data.reporterContext,
    runtime: data.runtime,
    diagnosticsIncluded: data.diagnosticsIncluded === true,
    attachmentRequested: data.attachmentRequested === true,
    attachment: normalizeAttachment(data.attachment),
    resolutionNote: typeof data.resolutionNote === 'string' ? data.resolutionNote : null,
    statusChangedAt: timestampToIso(data.statusChangedAt),
    statusChangedBy: String(data.statusChangedBy || ''),
    resolvedAt: nullableTimestampToIso(data.resolvedAt),
    resolvedBy: typeof data.resolvedBy === 'string' ? data.resolvedBy : null,
    createdAt: timestampToIso(data.createdAt),
    updatedAt: timestampToIso(data.updatedAt),
    updatedBy: String(data.updatedBy || ''),
  } as ProductFeedbackReport;
};

const requireReporterUid = (): string => {
  const uid = String(auth.currentUser?.uid || '').trim();
  if (!uid) throw new Error('Authenticated Product Feedback reporter is required.');
  return uid;
};

export const checkProductFeedbackAccess = async (
  scope: ProductFeedbackAccessScope = 'reporter'
): Promise<boolean> => {
  const callable = httpsCallable<{ scope: ProductFeedbackAccessScope }, { allowed?: unknown }>(
    functions,
    'checkProductFeedbackAccess'
  );
  const result = await callable({ scope });
  return result.data?.allowed === true;
};

export const createProductFeedbackReport = async (
  input: ProductFeedbackCreateInput
): Promise<ProductFeedbackCreateResult> => {
  const callable = httpsCallable<ProductFeedbackCreateInput, unknown>(
    functions,
    'createProductFeedbackReport'
  );
  const result = await callable(input);
  if (!isCreateResult(result.data)) {
    throw new Error('Product Feedback create returned an invalid response.');
  }
  return result.data;
};

export const readProductFeedbackReportFromServer = async (
  reportId: string
): Promise<ProductFeedbackCanonicalReadback> => {
  const snapshot = await getDocFromServer(doc(firestore, 'productFeedbackReports', reportId));
  if (!snapshot.exists()) {
    throw new Error('Canonical Product Feedback report does not exist.');
  }

  const data = snapshot.data() as Record<string, unknown>;
  const candidate = {
    reportId: snapshot.id,
    status: data.status,
    statusVersion: data.statusVersion,
  };
  if (!isCreateResult(candidate) || typeof data.createdBy !== 'string' || !data.createdBy) {
    throw new Error('Canonical Product Feedback report is inconsistent.');
  }

  return { ...candidate, createdBy: data.createdBy };
};

export const readCanonicalProductFeedbackReport = async (
  reportId: string
): Promise<ProductFeedbackReport> => {
  const snapshot = await getDocFromServer(doc(firestore, 'productFeedbackReports', reportId));
  if (!snapshot.exists()) throw new Error('Canonical Product Feedback report does not exist.');
  return parseReport(snapshot.id, snapshot.data());
};

export const readProductFeedbackDiagnostics = async (
  reportId: string
): Promise<ProductFeedbackDiagnosticsPayload> => {
  const snapshot = await getDocFromServer(
    doc(firestore, 'productFeedbackReports', reportId, 'payload', 'diagnostics')
  );
  if (!snapshot.exists()) throw new Error('Canonical Product Feedback diagnostics do not exist.');
  const data = snapshot.data() as ProductFeedbackDiagnosticsPayload;
  if (
    data.schemaVersion !== 1 ||
    typeof data.included !== 'boolean' ||
    !Array.isArray(data.events) ||
    typeof data.bytes !== 'number'
  ) {
    throw new Error('Canonical Product Feedback diagnostics are inconsistent.');
  }
  return data;
};

export const listProductFeedbackReports = async (
  filter: ProductFeedbackListFilter,
  cursor: ProductFeedbackPageCursor | null = null
): Promise<ProductFeedbackPage> => {
  const constraints: QueryConstraint[] = [];
  if (filter !== 'all') constraints.push(where('status', '==', filter));
  constraints.push(orderBy('createdAt', 'desc'), orderBy(documentId(), 'desc'));
  if (cursor) constraints.push(startAfter(cursor));
  constraints.push(limit(PRODUCT_FEEDBACK_PAGE_SIZE + 1));
  const snapshot = await getDocs(
    query(collection(firestore, 'productFeedbackReports'), ...constraints)
  );
  const pageDocs = snapshot.docs.slice(0, PRODUCT_FEEDBACK_PAGE_SIZE);
  return {
    reports: pageDocs.map((item) => parseReport(item.id, item.data())),
    nextCursor:
      snapshot.docs.length > PRODUCT_FEEDBACK_PAGE_SIZE
        ? (pageDocs[pageDocs.length - 1] as ProductFeedbackPageCursor)
        : null,
  };
};

export const listOwnProductFeedbackReportsFromServer = async (
  cursor: ProductFeedbackPageCursor | null = null
): Promise<ProductFeedbackPage> => {
  const uid = requireReporterUid();
  const constraints: QueryConstraint[] = [
    where('createdBy', '==', uid),
    orderBy('createdAt', 'desc'),
    orderBy(documentId(), 'desc'),
  ];
  if (cursor) constraints.push(startAfter(cursor));
  constraints.push(limit(REPORTER_PRODUCT_FEEDBACK_PAGE_SIZE + 1));
  const snapshot = await getDocsFromServer(
    query(collection(firestore, 'productFeedbackReports'), ...constraints)
  );
  const pageDocs = snapshot.docs.slice(0, REPORTER_PRODUCT_FEEDBACK_PAGE_SIZE);
  return {
    reports: pageDocs.map((item) => parseReport(item.id, item.data())),
    nextCursor:
      snapshot.docs.length > REPORTER_PRODUCT_FEEDBACK_PAGE_SIZE
        ? (pageDocs[pageDocs.length - 1] as ProductFeedbackPageCursor)
        : null,
  };
};

export const readOwnProductFeedbackReportFromServer = async (
  reportId: string
): Promise<ProductFeedbackReport | null> => {
  const uid = requireReporterUid();
  try {
    const snapshot = await getDocsFromServer(
      query(
        collection(firestore, 'productFeedbackReports'),
        where(documentId(), '==', reportId),
        where('createdBy', '==', uid),
        limit(1)
      )
    );
    const item = snapshot.docs[0];
    return item ? parseReport(item.id, item.data()) : null;
  } catch (error) {
    const code = String((error as { code?: unknown })?.code || '');
    if (code === 'permission-denied' || code.endsWith('/permission-denied')) return null;
    throw error;
  }
};

export const getProductFeedbackStatusCounts = async (): Promise<ProductFeedbackStatusCounts> => {
  const reports = collection(firestore, 'productFeedbackReports');
  const [open, investigating, resolved] = await Promise.all(
    (['open', 'investigating', 'resolved'] as ProductFeedbackStatus[]).map((status) =>
      getCountFromServer(query(reports, where('status', '==', status)))
    )
  );
  return {
    open: open.data().count,
    investigating: investigating.data().count,
    resolved: resolved.data().count,
  };
};

export const updateProductFeedbackReportStatus = async (
  input: ProductFeedbackStatusUpdateInput
): Promise<ProductFeedbackCreateResult> => {
  const callable = httpsCallable<ProductFeedbackStatusUpdateInput, unknown>(
    functions,
    'updateProductFeedbackReportStatus'
  );
  const result = await callable(input);
  if (!isCreateResult(result.data)) {
    throw new Error('Product Feedback status mutation returned an invalid response.');
  }
  return result.data;
};
